import {Component, Element, Event, EventEmitter, h, Host, Method, Prop, State, Watch} from '@stencil/core';
import {CaptchaBox} from './src/captcha-box';
import {Status} from './src/status';
import {getTranslation, NamedTranslation} from "./locale";
import {License} from "./src/license";
import {ErrorCode} from "./src/api/error/error-code";
import {ErrorModel} from "./src/api/error/error-model";
import {CheckIcon, ErrorIcon, LogoIcon, PrivacyText, SpinnerIcon, TrustcaptchaText} from "./icons";
import {Config} from "./config";

@Component({
  tag: 'trustcaptcha-component',
  styleUrl: 'trustcaptcha-component.css',
  shadow: true,
})
export class TrustcaptchaComponent {

  public design: Design = {
    rounding: {
      box: '6px',
      checkbox: '6px',
      invisibleHint: '6px',

    },
    theme: {
      light: {
        boxDefaultBackground: '#f9fafb', // gray-50
        boxDefaultText: '#374151', // gray-700
        boxDefaultBorder: '#d1d5db', // gray-300
        boxCheckboxBackground: '#ffffff', // white
        boxCheckboxBorder: '#d1d5db', // gray-300
        boxSuccessBackground: '#f9fafb', // gray-50
        boxSuccessBorder: '#86efac', // green-300
        invisibleHintBackground: '#ffffff', // white
      },
      dark: {
        boxDefaultBackground: '#1f2937', // gray-800
        boxDefaultText: '#e5e7eb', // gray-200
        boxDefaultBorder: '#374151', // gray-700
        boxCheckboxBackground: '#030712', // gray-900
        boxCheckboxBorder: '#374151', // gray-700
        boxSuccessBackground: '#1f2937', // gray-800
        boxSuccessBorder: '#15803d', // green-700
        invisibleHintBackground: '#1f2937', // gray-800
      }
    }
  }


  @Element()
  private hostElement: HTMLElement;

  @Prop()
  public sitekey: string;
  public trustcaptchaUrl: string = 'https://www.trustcomponent.com/en/products/captcha'
  @Prop()
  public privacyUrl: string;
  @Prop()
  public trustcaptchaApi: string = 'https://api.trustcomponent.com'
  @Prop()
  public framework: string | null = null;
  @Prop()
  public language: string = 'auto';
  @Prop()
  public customTranslations: string = ''
  @Prop()
  public theme: 'light' | 'dark' | 'media' = 'light';
  @Prop()
  public customDesign: string = '';
  @Prop({reflect: true})
  public autostart: boolean = true;
  @Prop()
  public tokenFieldName: string = 'tc-verification-token';
  @Prop()
  public bypassToken: string | null = '';
  @Prop()
  public mode: 'standard' | 'minimal' = 'standard';
  @Prop()
  public width: 'fixed' | 'full' = 'fixed'
  @Prop()
  public license: string | null = null;
  @Prop({reflect: true})
  public invisible: boolean = false;
  @Prop()
  public invisibleHint: 'hidden' | 'inline' | 'right-border' | 'right-bottom' = 'right-border';
  @Prop({reflect: true})
  public hideBranding: boolean = false;

  @Event()
  public captchaStarted: EventEmitter<void>;
  @Event()
  public captchaSolved: EventEmitter<string>;
  @Event()
  public captchaFailed: EventEmitter<string>;
  @Event()
  public captchaReset: EventEmitter<void>;

  @State()
  public captchaStatus = Status.START

  public captchaBox!: CaptchaBox;
  @State()
  public licenseObject?: License;
  @State()
  public translation: NamedTranslation;
  @State()
  public currentTheme: 'light' | 'dark' = 'light'
  public failureText: string = ''
  public mergedDesign: Design = this.design;


  public getConfig(): Config {
    return new Config(this.sitekey, this.privacyUrl, this.trustcaptchaUrl, this.trustcaptchaApi, this.framework, this.language, this.theme, this.currentTheme, this.autostart, this.tokenFieldName, this.bypassToken, this.mode, this.translation, this.hideBranding, this.invisible, this.licenseObject);
  }

  private mergeDeep(target, source) {
    for (const key in source) {
      if (source[key] instanceof Object && !(source[key] instanceof Array)) {
        target[key] = this.mergeDeep(target[key] || {}, source[key]);
      } else {
        target[key] = source[key];
      }
    }
    return target;
  }

  async componentWillLoad(): Promise<void> {

    this.translation = getTranslation(this.language, this.customTranslations);
    this.watchThemeUpdate()
    this.watchHtmlLangUpdate()

    this.captchaBox = new CaptchaBox(this.getConfig(), this.hostElement);
    this.captchaBox.statusChangedEvent.on('statusChanged', value => {
      this.captchaStatus = value;
      if (value == Status.RUNNING) {
        this.captchaStarted.emit();
      }
    });
    this.captchaBox.captchaFinishEvent.on("captchaSolved", () => {
      this.captchaSolved.emit(this.captchaBox.verificationToken)
    })
    this.captchaBox.captchaFinishEvent.on('captchaFailed', reason => {
      this.failureText = reason.message;
      this.captchaFailed.emit(reason)
    })
    this.captchaBox.captchaFinishEvent.on('captchaReset', () => {
      this.captchaReset.emit()
    })

    if (!this.sitekey) {
      this.captchaBox.setErrorStatus(new ErrorModel(ErrorCode.NO_SITE_KEY_SPECIFIED, 'No site key specified. Please provide a site key.'));
    }

    if (this.license) {
      this.licenseObject = await License.create(this.license, this.sitekey, errorModel => {
        this.captchaBox.setErrorStatus(errorModel);
      });
    }
    if (this.licenseObject) {
      if (this.hideBranding && !this.licenseObject?.customizabilityOption) {
        this.captchaBox.setErrorStatus(new ErrorModel(ErrorCode.OPTION_NOT_AVAILABLE, "Hide branding option not included in your current license. Please check the given license or contact the support."));
      }
      if (this.privacyUrl && !this.licenseObject?.customizabilityOption) {
        this.captchaBox.setErrorStatus(new ErrorModel(ErrorCode.OPTION_NOT_AVAILABLE, "Privacy url option not included in your current license. Please check the given license or contact the support."));
      }
      if (this.invisible && !this.licenseObject?.invisibilityOption) {
        this.captchaBox.setErrorStatus(new ErrorModel(ErrorCode.OPTION_NOT_AVAILABLE, "Invisible option not included in your current license. Please check the given license or contact the support."));
      }
    }

    let customizedDesign = {}
    if (this.customDesign && this.licenseObject && this.licenseObject?.customizabilityOption) {
      try {
        customizedDesign = JSON.parse(this.customDesign);
        this.mergedDesign = this.mergeDeep(structuredClone(this.design), customizedDesign);
      } catch (e) {
        console.error("Failed to parse custom design information");
      }
    }

    this.captchaBox.setup();
  }

  @Watch('language')
  watchLanguageUpdate() {
    this.translation = getTranslation(this.language, this.customTranslations);
    this.setNewConfig();
  }

  @Watch('theme')
  watchThemeUpdate() {
    switch (this.theme) {
      case "media":
        if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
          this.currentTheme = 'dark';
        } else {
          this.currentTheme = 'light';
        }
        break;
      case "dark":
        this.currentTheme = 'dark';
        break;
      case "light":
      default:
        this.currentTheme = 'light';
        break;
    }

    this.setNewConfig();
  }

  @Watch('customTranslations')
  watchCustomTranslationUpdate() {
    this.dummyState++;
  }

  @Watch('customDesign')
  watchCustomDesignUpdate() {
    this.dummyState++;
  }

  private setNewConfig(): void {
    const newConfig = this.getConfig();
    if (this.captchaBox) {
      this.captchaBox.setNewConfig(newConfig);
    }
  }

  watchHtmlLangUpdate(): void {
    const observer = new MutationObserver(mutations => {
      for (let mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'lang' && this.language == 'auto') {
          this.translation = getTranslation('auto', this.customTranslations);
          this.setNewConfig();
        }
      }
    });
    const config = {attributes: true, attributeFilter: ['lang']};
    observer.observe(document.documentElement, config);
  }

  @Method()
  public async startVerification() {
    this.captchaBox.startVerification();
  }

  @Method()
  public async reset() {
    this.captchaBox.reset();
  }


  @State() dummyState: number = 0;

  render() {
    return (
      <Host style={{
        '--round-box': this.mergedDesign.rounding.box,
        '--round-cb': this.mergedDesign.rounding.checkbox,
        '--round-ih': this.mergedDesign.rounding.invisibleHint,
        '--b-default-bg-l': this.mergedDesign.theme.light.boxDefaultBackground,
        '--b-default-text-l': this.mergedDesign.theme.light.boxDefaultText,
        '--b-default-border-l': this.mergedDesign.theme.light.boxDefaultBorder,
        '--b-default-bg-d': this.mergedDesign.theme.dark.boxDefaultBackground,
        '--b-default-border-d': this.mergedDesign.theme.dark.boxDefaultBorder,
        '--b-default-text-d': this.mergedDesign.theme.dark.boxDefaultText,
        '--b-checkbox-bg-l': this.mergedDesign.theme.light.boxCheckboxBackground,
        '--b-checkbox-border-l': this.mergedDesign.theme.light.boxCheckboxBorder,
        '--b-checkbox-bg-d': this.mergedDesign.theme.dark.boxCheckboxBackground,
        '--b-checkbox-border-d': this.mergedDesign.theme.dark.boxCheckboxBorder,
        '--b-success-bg-l': this.mergedDesign.theme.light.boxSuccessBackground,
        '--b-success-border-l': this.mergedDesign.theme.light.boxSuccessBorder,
        '--b-success-bg-d': this.mergedDesign.theme.dark.boxSuccessBackground,
        '--b-success-border-d': this.mergedDesign.theme.dark.boxSuccessBorder,
        '--ih-bg-l': this.mergedDesign.theme.light.invisibleHintBackground,
        '--ih-bg-d': this.mergedDesign.theme.dark.invisibleHintBackground,
      }}>
        {this.renderCheckboxDesign()}
        {this.invisible && this.invisibleHint !== 'hidden' && (
          <div>
            {this.invisibleHint === 'right-bottom' && (
              <div class="absolute bottom-5 right-5">
                {this.renderInvisibleHint()}
              </div>
            )}
            {this.invisibleHint === 'right-border' && (
              <div class="absolute right-5 transform -translate-y-full">
                {this.renderInvisibleHint()}
              </div>
            )}
            {this.invisibleHint === 'inline' && (
              <div class="relative">
                <div class="absolute -right-2 transform translate-x-full -translate-y-full">
                  {this.renderInvisibleHint()}
                </div>
              </div>
            )}
          </div>
        )}
      </Host>
    );
  }

  private renderCheckboxDesign() {
    return (
      <div class={{
        'p-2 border': true,
        'hidden': this.invisible && this.licenseObject?.invisibilityOption,
        'max-w-96': this.width === 'fixed',
        'max-w-full': this.width === 'full',
        'cursor-pointer': this.captchaBox.status === Status.START,
        'bg-red-50 border-red-300': this.currentTheme == 'light' && this.captchaBox.status === Status.FAILED,
        'bg-red-800 border-red-700': this.currentTheme == 'dark' && this.captchaBox.status === Status.FAILED,
      }} style={{
        borderRadius: 'var(--round-box)',
        backgroundColor:
          this.captchaBox.status === Status.START || this.captchaBox.status === Status.RUNNING
            ? this.currentTheme === 'light'
              ? 'var(--b-default-bg-l)'
              : 'var(--b-default-bg-d)'
            : this.captchaBox.status === Status.DONE
              ? this.currentTheme === 'light'
                ? 'var(--b-success-bg-l)'
                : 'var(--b-success-bg-d)'
              : '',
        borderColor:
          this.captchaBox.status === Status.START || this.captchaBox.status === Status.RUNNING
            ? this.currentTheme === 'light'
              ? 'var(--b-default-border-l)'
              : 'var(--b-default-border-d)'
            : this.captchaBox.status === Status.DONE
              ? this.currentTheme === 'light'
                ? 'var(--b-success-border-l)'
                : 'var(--b-success-border-d)'
              : '',
      }} onClick={() => this.startVerification()} onKeyDown={(e) => {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          this.startVerification();
        }
      }}>
        <div class='flex space-x-2' style={{
          color: this.currentTheme === 'light'
            ? 'var(--b-default-text-l)'
            : 'var(--b-default-text-d)',
        }}>
          <div class="flex justify-center items-center p-1">
            <div class='w-8 h-8 items-center content-center'>
              {this.captchaStatus == Status.START && this.renderCheckBoxIcon()}
              {this.captchaStatus == Status.RUNNING && <SpinnerIcon size={32} stroke={4}></SpinnerIcon>}
              {this.captchaStatus == Status.DONE && <CheckIcon size={32}></CheckIcon>}
              {this.captchaStatus == Status.FAILED && <ErrorIcon size={32}></ErrorIcon>}
            </div>
          </div>
          <div
            class='grow content-center text-sm font-medium'
            tabindex={this.captchaStatus === Status.START ? "0" : "-1"}
            role={this.captchaStatus === Status.START ? "button" : undefined}
            aria-label={
              this.captchaStatus === Status.START ? this.translation.ariaLabelStart :
              this.captchaStatus === Status.RUNNING ? this.translation.ariaLabelRunning :
              this.captchaStatus === Status.DONE ? this.translation.ariaLabelDone :
              this.captchaStatus === Status.FAILED ? this.translation.srFailed : ""
            }
          >
            {this.captchaStatus == Status.START && this.translation.boxStart}
            {this.captchaStatus == Status.RUNNING && this.translation.boxInProgress}
            {this.captchaStatus == Status.DONE && this.translation.boxCompleted}
            {this.captchaStatus == Status.FAILED &&
              <div class='leading-3'>
                <span class="text-red-500 text-xs">{this.failureText}</span>
              </div>
            }
          </div>
          <div class="sr-only" aria-live="polite" aria-atomic="true">
            {this.captchaStatus === Status.RUNNING && this.translation.srRunning}
            {this.captchaStatus === Status.DONE && this.translation.srDone}
            {this.captchaStatus === Status.FAILED && this.translation.srFailed}
          </div>
          <div class='content-center'>
            <div class='flex flex-col justify-end space-x-2 text-xs'>
              {!(this.hideBranding && this.licenseObject?.customizabilityOption) &&
                <TrustcaptchaText currentTheme={this.currentTheme} trustcaptchaUrl={this.trustcaptchaUrl} translation={this.translation}></TrustcaptchaText>
              }
              {(this.privacyUrl && this.licenseObject?.customizabilityOption) &&
                <PrivacyText privacyUrl={this.privacyUrl} translation={this.translation}></PrivacyText>
              }
            </div>
          </div>
        </div>
      </div>
    );
  }

  private renderCheckBoxIcon() {
    return (
      <div class='h-8 w-8 p-2 border' style={{
        borderRadius: 'var(--round-cb)',
        backgroundColor: this.currentTheme === 'light' ? 'var(--b-checkbox-bg-l)' : 'var(--b-checkbox-bg-d)',
        borderColor: this.currentTheme === 'light' ? 'var(--b-checkbox-border-l)' : 'var(--b-checkbox-border-d)',
      }}></div>
    )
  }

  private renderInvisibleHint() {
    return (
      <div class={{
        'p-3 rounded-md z-[1000] shadow-lg main-container flex gap-4': true,
        'hidden': this.captchaBox.status === Status.START,
        'bg-red-50 border-red-300': this.currentTheme == 'light' && this.captchaBox.status === Status.FAILED,
        'bg-red-800 border-red-700': this.currentTheme == 'dark' && this.captchaBox.status === Status.FAILED
      }} style={{
        borderRadius: 'var(--round-ih)',
        color: this.currentTheme === 'light' ? 'var(--b-default-text-l)' : 'var(--b-default-text-d)',
        backgroundColor: this.currentTheme === 'light' ? 'var(--ih-bg-l)' : 'var(--ih-bg-d)'
      }}>
        <div class="flex justify-center items-center">
          <div class={{
            'iconLight': this.currentTheme == 'light',
            'iconDark': this.currentTheme == 'dark',
          }}>
            <LogoIcon size={32}></LogoIcon>
          </div>
          {this.captchaStatus == Status.RUNNING && <SpinnerIcon size={20} stroke={4}></SpinnerIcon>}
          {this.captchaStatus == Status.DONE && <CheckIcon size={20}></CheckIcon>}
          {this.captchaStatus == Status.FAILED && <ErrorIcon size={20}></ErrorIcon>}
        </div>
        <div class='hidden-container content-center'>
          <div class='flex flex-col justify-end space-x-2 text-xs'>
            {!(this.hideBranding && this.licenseObject?.customizabilityOption) &&
              <TrustcaptchaText currentTheme={this.currentTheme} trustcaptchaUrl={this.trustcaptchaUrl} translation={this.translation}></TrustcaptchaText>
            }
            {this.privacyUrl &&
              <PrivacyText privacyUrl={this.privacyUrl} translation={this.translation}></PrivacyText>
            }
          </div>
        </div>
      </div>
    )
  }
}

export interface Design {
  rounding: Rounding,
  theme: Theme,
}

export interface Rounding {
   box: string;
   checkbox: string;
   invisibleHint: string;
}
export interface Theme {
  light: Colors,
  dark: Colors,
}

export interface Colors {
  boxDefaultBackground: string;
  boxDefaultText: string;
  boxDefaultBorder: string;
  boxCheckboxBackground: string;
  boxCheckboxBorder: string;
  boxSuccessBackground: string;
  boxSuccessBorder: string;
  invisibleHintBackground: string;
}
